/**
 * ================================================================================================
 *  בקשת שינוי מסחרי — the path for what an office cannot change by itself
 * ================================================================================================
 *
 * The Owner, 2026-11-02:
 *
 *     *"לקוח בעל תנאי מסחרי מיוחד — מסכים למסלול בקשה כאשר השינוי אינו ניתן לביצוע תוך שימור
 *      התנאים שלו. אין לבטל או להחליף תנאי מוסכם בשקט."*
 *
 * `billing.change.request` was seeded in 0005 and granted in 0010; this is the first surface to
 * reach it, and 0047 the first table.
 *
 * ── THE PANEL PROMISES NOTHING ABOUT WHAT WILL HAPPEN ─────────────────────────────────────────
 *
 * It says a request was recorded, and it shows what was answered when somebody answers. It does
 * NOT say *"your plan will be changed"*, because the thing on the other end is a conversation and
 * not a transaction — resolving a request writes an answer and changes no terms, by the service's
 * own method surface. A panel that implied otherwise would be the silent substitution the Owner's
 * second sentence forbids, made by the interface instead of by an operator.
 *
 * ── NO PRICE FIELD, EITHER DIRECTION ──────────────────────────────────────────────────────────
 *
 * 08 §8.4 keeps a figure out of a tenant-side billing read, and a field here would invite a
 * customer to propose one into a record an operator reads as a number. They describe what they
 * want; the price is decided where the price book and dual control already are.
 */

import { useCallback, useState, type FormEvent } from 'react';

import { ActionFailureNotice } from './ActionFailureNotice';
import { ServerRead } from './ServerRead';
import { StateBlock } from './StateBlock';
import { StatusChip } from './StatusChip';
import { callOperation } from '../api/operations';
import { classifyActionError } from '../api/actionOutcome';
import type { ActionFailure } from '../api/actionOutcome';
import type { ChangeRequestRead } from '../api/contract';
import { useServerData } from '../api/useServerData';

/**
 * WHAT TO CALL EACH KIND, AND WHAT IT IS FOR.
 *
 * The four are `CK_ccr_kind`'s own and are not re-listed as a vocabulary: the server refuses
 * anything outside the set by name, and this map supplies WORDING. A fifth value added to the
 * constraint renders as its own code here rather than as a silent default — the same rule
 * `billingOutcomeHe` follows.
 */
const KIND_WORDS: Readonly<Record<string, string>> = {
  plan_change: 'מעבר לתכנית אחרת',
  quantity_change: 'שינוי כמויות — חברות או משתמשים',
  module: 'הוספה או הסרה של מודול',
  other: 'משהו אחר',
};

const KIND_ORDER: readonly string[] = ['plan_change', 'quantity_change', 'module', 'other'];

/** `CK_ccr_status`'s four, in the office's own words. */
function statusWords(status: string): { labelHe: string; tone: 'ok' | 'warn' | 'error' | 'muted' } {
  if (status === 'open') return { labelHe: 'ממתינה לטיפול', tone: 'warn' };
  if (status === 'in_progress') return { labelHe: 'בטיפול', tone: 'warn' };
  if (status === 'resolved') return { labelHe: 'נענתה', tone: 'ok' };
  if (status === 'declined') return { labelHe: 'נדחתה', tone: 'error' };
  // AS IT CAME. A status this build has no wording for is shown, never defaulted to one of the four.
  return { labelHe: status, tone: 'muted' };
}

export function ChangeRequestPanel(): JSX.Element {
  const read = useCallback(() => callOperation('tenant.change_request.read', {}), []);
  const history = useServerData(read);

  const [kind, setKind] = useState('plan_change');
  const [detail, setDetail] = useState('');
  const [sending, setSending] = useState(false);
  const [failure, setFailure] = useState<ActionFailure | null>(null);
  const [submitted, setSubmitted] = useState(false);

  async function submit(event: FormEvent): Promise<void> {
    event.preventDefault();
    if (sending || detail.trim() === '') return;
    setSending(true);
    setFailure(null);
    try {
      await callOperation('tenant.change_request.submit', {
        body: { requestKind: kind, detail: detail.trim() },
      });
      setSubmitted(true);
      setDetail('');
      history.reload();
    } catch (err) {
      setFailure(classifyActionError(err));
    } finally {
      setSending(false);
    }
  }

  return (
    <section className="panel" data-area="change-request">
      <h2 className="panel__title">בקשת שינוי מסחרי</h2>
      <p className="notice__body">
        שינוי שאי אפשר לבצע כאן — מעבר לתכנית אחרת, שינוי כמויות או תנאים מיוחדים — נרשם כבקשה
        ומטופל מול הפלטפורמה. התנאים הקיימים נשארים כפי שהם עד שמתקבלת תשובה.
      </p>

      {/*
        THE ANSWER LIVES ABOVE THE READ IT INVALIDATES. Submitting reloads the history, which
        returns `<ServerRead>` to loading; anything held under it would be destroyed by the
        refresh that proves the write worked.
      */}
      {failure === null ? null : (
        <ActionFailureNotice failure={failure} onStepUpSatisfied={() => setFailure(null)} />
      )}
      {!submitted ? null : (
        <p className="notice__body" data-area="change-request-submitted">
          הבקשה נרשמה. היא מופיעה למטה, ותקבל תשובה כאן.
        </p>
      )}

      <form className="form form--inline" onSubmit={(e) => void submit(e)}>
        <label className="field">
          <span className="field__label">סוג הבקשה</span>
          {/*
            RADIO BUTTONS AND NOT A DROPDOWN. 09 §3.4's allowlist exists for the controls that
            genuinely need one; four fixed options with a sentence each are a set a person reads,
            and a `<select>` would hide three of them behind a click for no gain.
          */}
          <span className="field__choices">
            {KIND_ORDER.map((code) => (
              <label key={code} className="field__choice">
                <input
                  type="radio"
                  name="change-request-kind"
                  value={code}
                  checked={kind === code}
                  onChange={() => setKind(code)}
                />
                {KIND_WORDS[code] ?? code}
              </label>
            ))}
          </span>
        </label>
        <label className="field">
          <span className="field__label">מה נדרש</span>
          <textarea
            className="field__input"
            name="change-request-detail"
            value={detail}
            maxLength={2000}
            onChange={(e) => setDetail(e.currentTarget.value)}
          />
          <span className="field__hint">
            במילים שלך. אין צורך לנקוב במחיר — התמחור נקבע מול הפלטפורמה.
          </span>
        </label>
        <button
          type="submit"
          className="btn btn--primary"
          disabled={sending || detail.trim() === ''}
        >
          {sending ? 'שולח…' : 'שליחת הבקשה'}
        </button>
      </form>

      <ServerRead result={history.result} whatHe="את הבקשות שלך" skeletonRows={2}>
        {(data) =>
          data.requests.length === 0 ? (
            <StateBlock
              titleHe="לא נשלחו בקשות"
              bodyHe="כשתישלח בקשה היא תופיע כאן, יחד עם התשובה שתתקבל."
              tone="empty"
            />
          ) : (
            <ul className="list" data-area="change-request-history">
              {data.requests.map((request) => (
                <li key={request.changeRequestUid} className="list__row">
                  <RequestRow request={request} />
                </li>
              ))}
            </ul>
          )
        }
      </ServerRead>
    </section>
  );
}

function RequestRow({ request }: { readonly request: ChangeRequestRead }): JSX.Element {
  const status = statusWords(request.status);
  return (
    <div className="list__main">
      <p className="notice__body">
        <StatusChip labelHe={status.labelHe} tone={status.tone} />
        {` ${KIND_WORDS[request.requestKind] ?? request.requestKind} · `}
        <span dir="ltr">{request.createdAtUtc.slice(0, 10)}</span>
      </p>
      <p className="notice__body">{request.detail}</p>
      {/*
        THE ANSWER, WHEN THERE IS ONE, AND THE ABSENCE WHEN THERE IS NOT. An open request with no
        line here would read as a request nobody looked at, which is true and should be SAID --
        `CK_ccr_resolved_has_answer` guarantees that a closed row has words, so a closed row with
        nothing here cannot happen and an open one is told plainly.
      */}
      {request.resolution === null ? (
        <p className="field__hint">טרם התקבלה תשובה.</p>
      ) : (
        <p className="notice__body">
          {`תשובה: ${request.resolution}`}
          {request.resolvedByDisplayName === null ? '' : ` — ${request.resolvedByDisplayName}`}
        </p>
      )}
    </div>
  );
}
